import React, { useEffect, useState } from 'react';
import {
    Alert,
    Card,
    Col,
    Empty,
    Input,
    InputNumber,
    Radio,
    Row,
    Skeleton,
    Space,
    Switch,
    Tabs,
    Typography,
} from 'antd';
import {
    BellOutlined,
    ClockCircleOutlined,
    InfoCircleOutlined,
    ThunderboltOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { aiAdminApi, liveSettingsKeys } from '../../../services/ai-admin-api';
import { useSubsystemEditorContext } from '../hooks/useSubsystemDraftEditor';
import { SubsystemDraftActions } from '../components/SubsystemDraftActions';
import { PromptEditor } from '../shared/PromptEditor';
import {
    ProviderModelTempForm,
    ProviderModelTempValue,
    ProviderId,
} from '../shared/ProviderModelTempForm';

const { Text, Paragraph } = Typography;

interface ResponseMonitoringAiPayload {
    schemaVersion: 1;
    systemSettingsKeys: Record<string, string | undefined>;
    configTableRows: Record<string, never>;
}

function asBool(v: string | undefined): boolean {
    return (v ?? 'false').toString().toLowerCase() === 'true';
}

function asInt(v: string | undefined, fallback = 0): number {
    if (v === undefined || v === null || v === '') return fallback;
    const n = Number(v);
    return Number.isFinite(n) ? Math.round(n) : fallback;
}

function parseModelList(raw: string | undefined): Array<{ id: string; label?: string }> {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((entry: unknown) => {
                if (typeof entry === 'string') return { id: entry };
                if (entry && typeof entry === 'object') {
                    const e = entry as { id?: string; label?: string };
                    if (e.id) return { id: e.id, label: e.label };
                }
                return null;
            })
            .filter((m): m is { id: string; label?: string } => m !== null);
    } catch {
        return [];
    }
}

function guessProvider(modelId: string | undefined | null): ProviderId {
    if (!modelId) return 'anthropic';
    if (modelId.startsWith('claude') || modelId.startsWith('anthropic')) return 'anthropic';
    return 'openai';
}

/**
 * Configure tab body for Response Monitoring AI.
 *
 * Pure system_settings — no config tables. Vertical sidebar tabs split the
 * 16 keys into Schedule / Thresholds / LLM / Notifications. The runtime cron
 * is owned by the response-monitoring service; this page only edits the
 * settings it consumes.
 */
export const ResponseMonitoringAiConfigurePage: React.FC = () => {
    const editor = useSubsystemEditorContext<ResponseMonitoringAiPayload>();
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    const [anthropicModels, setAnthropicModels] = useState<Array<{ id: string; label?: string }>>([]);
    const [openaiModels, setOpenaiModels] = useState<Array<{ id: string; label?: string }>>([]);
    const [googleModels, setGoogleModels] = useState<Array<{ id: string; label?: string }>>([]);
    // Deep-link support — ?section=llm sends the AI System read-only summary's
    // Change button straight to the LLM provider editor below.
    const [searchParams] = useSearchParams();
    const sectionParam = searchParams.get('section');
    const initialSection =
        sectionParam === 'thresholds' ||
        sectionParam === 'llm' ||
        sectionParam === 'notifications'
            ? sectionParam
            : 'schedule';

    useEffect(() => {
        if (!jwt) return;
        aiAdminApi
            .getSubsystem(jwt, 'models_providers')
            .then((mp) => {
                const keys = liveSettingsKeys(mp);
                setAnthropicModels(
                    parseModelList(keys.AVAILABLE_ANTHROPIC_MODELS),
                );
                setOpenaiModels(
                    parseModelList(keys.AVAILABLE_OPENAI_MODELS),
                );
                setGoogleModels(
                    parseModelList(keys.AVAILABLE_GOOGLE_MODELS),
                );
            })
            .catch(() => {
                // Non-fatal — admin can still type a model id manually.
            });
    }, [jwt]);

    if (editor.isLoading || !editor.payload) {
        return <Skeleton active paragraph={{ rows: 12 }} />;
    }

    const settings = editor.payload.systemSettingsKeys ?? {};
    if (!settings || Object.keys(settings).length === 0) {
        return <Empty description="No response-monitoring settings — has the v1 baseline been seeded?" />;
    }

    const setSetting = (key: string, value: string) => {
        editor.setPayload((prev) => ({
            ...prev!,
            systemSettingsKeys: { ...prev!.systemSettingsKeys, [key]: value },
        }));
    };

    const setManySettings = (patch: Record<string, string>) => {
        editor.setPayload((prev) => ({
            ...prev!,
            systemSettingsKeys: { ...prev!.systemSettingsKeys, ...patch },
        }));
    };

    return (
        <div>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
                <Text type="secondary">
                    Scheduled review of survey response volumes per brand. Thresholds drive
                    severity (low / warning / critical); the LLM generates the natural-language
                    alert message; recipients dictate who gets pinged.
                </Text>
                <SubsystemDraftActions
                    detail={editor.detail}
                    isDirty={editor.isDirty}
                    isSaving={editor.isSaving}
                    isPublishing={editor.isPublishing}
                    isDiscarding={editor.isDiscarding}
                    onSaveDraft={() => editor.saveDraft()}
                    onDiscardDraft={() => editor.discardDraft()}
                />
            </Space>

            <Card>
                <Tabs
                    tabPosition="left"
                    defaultActiveKey={initialSection}
                    style={{ minHeight: 600 }}
                    items={[
                        {
                            key: 'schedule',
                            label: (
                                <span>
                                    <ClockCircleOutlined style={{ marginRight: 8, color: '#3b82f6' }} />
                                    Schedule
                                </span>
                            ),
                            children: <ScheduleTab settings={settings} setSetting={setSetting} />,
                        },
                        {
                            key: 'thresholds',
                            label: (
                                <span>
                                    <WarningOutlined style={{ marginRight: 8, color: '#f97316' }} />
                                    Thresholds
                                </span>
                            ),
                            children: <ThresholdsTab settings={settings} setSetting={setSetting} />,
                        },
                        {
                            key: 'llm',
                            label: (
                                <span>
                                    <ThunderboltOutlined style={{ marginRight: 8, color: '#10b981' }} />
                                    LLM
                                </span>
                            ),
                            children: (
                                <LlmTab
                                    settings={settings}
                                    setSetting={setSetting}
                                    setManySettings={setManySettings}
                                    anthropicModels={anthropicModels}
                                    openaiModels={openaiModels}
                                    googleModels={googleModels}
                                />
                            ),
                        },
                        {
                            key: 'notifications',
                            label: (
                                <span>
                                    <BellOutlined style={{ marginRight: 8, color: '#ef4444' }} />
                                    Notifications
                                </span>
                            ),
                            children: <NotificationsTab settings={settings} setSetting={setSetting} />,
                        },
                    ]}
                />
            </Card>
        </div>
    );
};

interface SettingsTabProps {
    settings: Record<string, string | undefined>;
    setSetting: (key: string, value: string) => void;
}

const ScheduleTab: React.FC<SettingsTabProps> = ({ settings, setSetting }) => {
    const enabled = asBool(settings.RESPONSE_MONITORING_ENABLED);
    const schedule = settings.RESPONSE_MONITORING_SCHEDULE ?? '';
    const checkPeriod = settings.RESPONSE_MONITORING_CHECK_PERIOD ?? 'weekly';
    const gracePeriods = asInt(settings.RESPONSE_MONITORING_GRACE_PERIODS, 1);

    return (
        <div>
            <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: 16 }}
                message="Scheduled response review"
                description="A distributed cron job runs at the schedule below. For each active brand it counts responses inside the check period, compares against the thresholds in the next tab, and fires a message via the LLM tab when a brand is below threshold."
            />

            <Row gutter={16}>
                <Col xs={24} md={6}>
                    <Space direction="vertical" size={2}>
                        <Text strong>Monitoring enabled</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            <code>RESPONSE_MONITORING_ENABLED</code>
                        </Text>
                        <Switch
                            checked={enabled}
                            onChange={(checked) =>
                                setSetting('RESPONSE_MONITORING_ENABLED', checked ? 'true' : 'false')
                            }
                        />
                    </Space>
                </Col>
                <Col xs={24} md={9}>
                    <Text strong>Schedule (cron expression)</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        <code>RESPONSE_MONITORING_SCHEDULE</code> — when the monitoring job
                        runs. Default <code>0 6 * * 1</code> (Mondays at 06:00).
                    </Text>
                    <br />
                    <Input
                        style={{ marginTop: 4, fontFamily: 'monospace' }}
                        value={schedule}
                        onChange={(e) => setSetting('RESPONSE_MONITORING_SCHEDULE', e.target.value)}
                        placeholder="0 6 * * 1"
                    />
                </Col>
                <Col xs={24} md={9}>
                    <Text strong>Check period</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        <code>RESPONSE_MONITORING_CHECK_PERIOD</code> — window of responses to
                        evaluate against thresholds.
                    </Text>
                    <br />
                    <Radio.Group
                        style={{ marginTop: 4 }}
                        value={checkPeriod}
                        onChange={(e) =>
                            setSetting('RESPONSE_MONITORING_CHECK_PERIOD', e.target.value)
                        }
                        options={[
                            { value: 'daily', label: 'Daily' },
                            { value: 'weekly', label: 'Weekly' },
                            { value: 'monthly', label: 'Monthly' },
                        ]}
                        optionType="button"
                    />
                </Col>
            </Row>

            <div style={{ marginTop: 24 }}>
                <Text strong>Grace periods for new brands</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                    <code>RESPONSE_MONITORING_GRACE_PERIODS</code> — number of consecutive
                    monitoring runs to skip alerts for newly-created brands so they have time to
                    accumulate responses before getting flagged.
                </Text>
                <br />
                <InputNumber
                    min={0}
                    step={1}
                    style={{ width: 180, marginTop: 4 }}
                    value={gracePeriods}
                    onChange={(v) => setSetting('RESPONSE_MONITORING_GRACE_PERIODS', String(v ?? 0))}
                    addonAfter="runs"
                />
            </div>
        </div>
    );
};

const ThresholdsTab: React.FC<SettingsTabProps> = ({ settings, setSetting }) => {
    const critical = asInt(settings.RESPONSE_MONITORING_THRESHOLD_CRITICAL, 0);
    const warning = asInt(settings.RESPONSE_MONITORING_THRESHOLD_WARNING, 10);
    const low = asInt(settings.RESPONSE_MONITORING_THRESHOLD_LOW, 30);

    return (
        <div>
            <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: 16 }}
                message="Severity thresholds (response counts)"
                description="At end-of-period, the runtime counts responses per brand and assigns a severity: critical at or below the critical count, warning between critical and warning, low between warning and low. Brands with response counts above the low threshold are healthy."
            />

            <Row gutter={16}>
                <Col xs={24} md={8}>
                    <Text strong>Critical</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        <code>RESPONSE_MONITORING_THRESHOLD_CRITICAL</code>
                    </Text>
                    <br />
                    <InputNumber
                        min={0}
                        step={1}
                        style={{ width: '100%', marginTop: 4 }}
                        value={critical}
                        onChange={(v) =>
                            setSetting('RESPONSE_MONITORING_THRESHOLD_CRITICAL', String(v ?? 0))
                        }
                        addonAfter="responses"
                    />
                </Col>
                <Col xs={24} md={8}>
                    <Text strong>Warning</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        <code>RESPONSE_MONITORING_THRESHOLD_WARNING</code>
                    </Text>
                    <br />
                    <InputNumber
                        min={0}
                        step={1}
                        style={{ width: '100%', marginTop: 4 }}
                        value={warning}
                        onChange={(v) =>
                            setSetting('RESPONSE_MONITORING_THRESHOLD_WARNING', String(v ?? 0))
                        }
                        addonAfter="responses"
                    />
                </Col>
                <Col xs={24} md={8}>
                    <Text strong>Low</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        <code>RESPONSE_MONITORING_THRESHOLD_LOW</code>
                    </Text>
                    <br />
                    <InputNumber
                        min={0}
                        step={1}
                        style={{ width: '100%', marginTop: 4 }}
                        value={low}
                        onChange={(v) =>
                            setSetting('RESPONSE_MONITORING_THRESHOLD_LOW', String(v ?? 0))
                        }
                        addonAfter="responses"
                    />
                </Col>
            </Row>

            {!(critical <= warning && warning <= low) && (
                <Alert
                    style={{ marginTop: 16 }}
                    type="warning"
                    showIcon
                    message="Threshold ordering looks off"
                    description={`Expected critical ≤ warning ≤ low. Currently: critical=${critical}, warning=${warning}, low=${low}.`}
                />
            )}
        </div>
    );
};

interface LlmTabProps extends SettingsTabProps {
    setManySettings: (patch: Record<string, string>) => void;
    anthropicModels: Array<{ id: string; label?: string }>;
    openaiModels: Array<{ id: string; label?: string }>;
    googleModels: Array<{ id: string; label?: string }>;
}

const LlmTab: React.FC<LlmTabProps> = ({
    settings,
    setSetting,
    setManySettings,
    anthropicModels,
    openaiModels,
    googleModels,
}) => {
    const primaryValue: ProviderModelTempValue = {
        provider: (settings.RESPONSE_MONITORING_AI_PROVIDER as ProviderId) || guessProvider(settings.RESPONSE_MONITORING_AI_MODEL),
        model: settings.RESPONSE_MONITORING_AI_MODEL ?? '',
        temperature: Number(settings.RESPONSE_MONITORING_AI_PRIMARY_TEMPERATURE ?? 0.7),
    };

    const fallbackValue: ProviderModelTempValue = {
        provider: (settings.RESPONSE_MONITORING_AI_FALLBACK_PROVIDER as ProviderId) || guessProvider(settings.RESPONSE_MONITORING_AI_FALLBACK_MODEL),
        model: settings.RESPONSE_MONITORING_AI_FALLBACK_MODEL ?? '',
        temperature: Number(settings.RESPONSE_MONITORING_AI_FALLBACK_TEMPERATURE ?? 0.7),
    };

    const handlePrimaryChange = (next: ProviderModelTempValue) => {
        setManySettings({
            RESPONSE_MONITORING_AI_PROVIDER: next.provider,
            RESPONSE_MONITORING_AI_MODEL: next.model,
            RESPONSE_MONITORING_AI_PRIMARY_TEMPERATURE: String(next.temperature),
        });
    };

    const handleFallbackChange = (next: ProviderModelTempValue) => {
        setManySettings({
            RESPONSE_MONITORING_AI_FALLBACK_PROVIDER: next.provider,
            RESPONSE_MONITORING_AI_FALLBACK_MODEL: next.model,
            RESPONSE_MONITORING_AI_FALLBACK_TEMPERATURE: String(next.temperature),
        });
    };

    return (
        <div>
            <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: 16 }}
                message="LLM that authors the alert message"
                description="When a brand falls below threshold, the runtime calls this LLM with the system message below and a structured input describing the brand and counts. The LLM's reply is the natural-language message sent to recipients."
            />

            <Card title="Provider — primary + fallback" size="small" style={{ marginBottom: 16 }}>
                <ProviderModelTempForm
                    label="Primary"
                    value={primaryValue}
                    onChange={handlePrimaryChange}
                    anthropicModels={anthropicModels}
                    openaiModels={openaiModels}
                    googleModels={googleModels}
                />
                <div style={{ marginTop: 12 }}>
                    <ProviderModelTempForm
                        label="Fallback"
                        value={fallbackValue}
                        onChange={handleFallbackChange}
                        anthropicModels={anthropicModels}
                        openaiModels={openaiModels}
                        googleModels={googleModels}
                    />
                </div>
            </Card>

            <PromptEditor
                consumerSubsystem="response_monitoring_ai"
                label="System message — RESPONSE_MONITORING_AI_SYSTEM_MESSAGE"
                value={settings.RESPONSE_MONITORING_AI_SYSTEM_MESSAGE ?? ''}
                onChange={(v) => setSetting('RESPONSE_MONITORING_AI_SYSTEM_MESSAGE', v)}
                rows={10}
            />
        </div>
    );
};

const NotificationsTab: React.FC<SettingsTabProps> = ({ settings, setSetting }) => {
    const notifySuperAdmins = asBool(settings.RESPONSE_MONITORING_NOTIFY_SUPER_ADMINS);
    const messageRecipients = settings.RESPONSE_MONITORING_MESSAGE_RECIPIENTS ?? '';

    return (
        <div>
            <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: 16 }}
                message="Who gets pinged"
                description="The Notify Super Admins switch sends platform-wide alerts to every SUPER_ADMIN in addition to whatever the message recipients setting routes to."
            />

            <Row gutter={16}>
                <Col xs={24} md={8}>
                    <Space direction="vertical" size={2}>
                        <Text strong>Notify SUPER_ADMINs</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            <code>RESPONSE_MONITORING_NOTIFY_SUPER_ADMINS</code>
                        </Text>
                        <Switch
                            checked={notifySuperAdmins}
                            onChange={(checked) =>
                                setSetting(
                                    'RESPONSE_MONITORING_NOTIFY_SUPER_ADMINS',
                                    checked ? 'true' : 'false',
                                )
                            }
                        />
                    </Space>
                </Col>
                <Col xs={24} md={16}>
                    <Text strong>Default message recipients</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        <code>RESPONSE_MONITORING_MESSAGE_RECIPIENTS</code> — who in the flagged
                        organisation receives the AI-authored message.
                    </Text>
                    <br />
                    <Radio.Group
                        style={{ marginTop: 4 }}
                        value={messageRecipients || 'organisation_admins'}
                        onChange={(e) =>
                            setSetting('RESPONSE_MONITORING_MESSAGE_RECIPIENTS', e.target.value)
                        }
                        options={[
                            { value: 'organisation_admins', label: 'Organisation admins only' },
                            { value: 'all_users', label: 'All organisation users' },
                        ]}
                        optionType="button"
                    />
                </Col>
            </Row>
        </div>
    );
};
